import { useEffect, useState } from "react";
import { Plus, Trash2, Webhook as WebhookIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";
import { formatDate } from "@/lib/format";

const EVENTS = ["payment.created", "payment.pending", "payment.detected", "payment.confirmed", "payment.expired", "payment.failed", "payout.created", "payout.completed", "payout.failed"];

export default function Webhooks() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/webhooks").then(({ data }) => setData(data)).catch(() => setData({ webhooks: [], deliveries: [] }));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/webhooks", { url, events });
      toast.success("Webhook eklendi.");
      setOpen(false); setUrl(""); setEvents([]);
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/webhooks/${id}`); load(); };
  const toggleEv = (e) => setEvents((p) => p.includes(e) ? p.filter((x) => x !== e) : [...p, e]);

  if (!data) return <Spinner />;

  return (
    <div>
      <PageHeader title="Webhooklar" desc="Ödeme olaylarını sisteminize iletin."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-webhook-btn" className="rounded-full"><Plus size={16} /> Webhook Ekle</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Yeni Webhook</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label className="text-sm">Endpoint URL</Label><Input required value={url} onChange={(e) => setUrl(e.target.value)} data-testid="webhook-url" className="mt-1.5" placeholder="https://siteniz.com/webhook" /></div>
                <div>
                  <Label className="text-sm">Olaylar (boş = tümü)</Label>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {EVENTS.map((ev) => (
                      <button type="button" key={ev} onClick={() => toggleEv(ev)} className={`rounded-full border px-2.5 py-1 text-xs font-mono ${events.includes(ev) ? "border-primary bg-primary text-white" : "border-border"}`}>{ev}</button>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={saving} className="w-full rounded-full">{saving ? <Loader2 className="animate-spin" size={16} /> : "Ekle"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {data.webhooks.length === 0 ? (
        <EmptyState icon={WebhookIcon} title="Henüz webhook yok" desc="Ödeme olaylarını almak için bir endpoint ekleyin." />
      ) : (
        <div className="space-y-3 mb-8">
          {data.webhooks.map((w) => (
            <Card key={w.id} className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-mono text-sm truncate">{w.url}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{w.events?.length ? w.events.join(", ") : "Tüm olaylar"}</p>
              </div>
              <Button variant="outline" size="icon" className="text-red-500 shrink-0" onClick={() => del(w.id)}><Trash2 size={15} /></Button>
            </Card>
          ))}
        </div>
      )}

      <h3 className="text-base font-bold mb-3">Son İletimler</h3>
      {data.deliveries.length === 0 ? (
        <p className="text-sm text-muted-foreground">Henüz iletim yok.</p>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40"><tr className="text-left text-xs text-muted-foreground">
              <th className="px-4 py-3 font-medium">Olay</th><th className="px-4 py-3 font-medium">URL</th><th className="px-4 py-3 font-medium">Durum</th><th className="px-4 py-3 font-medium">Tarih</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {data.deliveries.map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-3 font-mono text-xs">{d.event}</td>
                  <td className="px-4 py-3 text-xs truncate max-w-[180px]">{d.url}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${d.status === "delivered" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>{d.status === "delivered" ? "İletildi" : "Başarısız"}</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
